import { action, computed, observable, reaction, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
} from '@/features/shell'
import type { TxfWorklayerJobStore } from '@/features/jobs/details/tax-type/tax-prep-organizer/TxfWorklayerJobStore'
import {
  type TaxPreparationData,
  type TxfWorklayerJob,
  type FilingYear,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { TxfWorklayerJobExtractionStatus } from '@/features/jobs/details/tax-type/tax-prep-organizer/models/TxfWorklayerJobExtractionStatus'
import { TaxPrepOrganizerViewModel } from './TaxPrepOrganizerViewModel'
import { SwitchYearDialogViewModel } from './components/SwitchYearDialog/SwitchYearDialogViewModel'
import { ConnectWithProDialogViewModel } from './components/ConnectWithProDialog/ConnectWithProDialogViewModel'
import type { InfoGatheringStore } from '../info-gathering/InfoGatheringStore'
import type { Checklist } from '../info-gathering/Checklist'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'
import { links } from '@/utils/link'
import type { TxfJobViewModel } from '../txf/TxfJobViewModel'
import type { SubmittedJob } from '../../SubmittedJob'
import { asAtom } from '@/utils/mobx-util'
import { countdown, withAbortSignal } from '@/utils/rx-util'
import { timer } from 'rxjs/internal/observable/timer'
import { takeWhile } from 'rxjs/internal/operators/takeWhile'
import { from, switchMap, tap } from 'rxjs'

export class TaxPrepOrganizerWrapperViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  private _lastDate = new Date(Date.now() + 10 * 60 * 1000)
  private _countDown = asAtom(countdown(this._lastDate), 0)
  private _disposeTimerReaction: (() => void) | null = null

  @observable accessor txfWorklayerJob: TxfWorklayerJob | null = null
  @observable accessor checklist: Checklist | null = null
  @observable accessor selectedYear: FilingYear | null = null
  @observable accessor taxPrepData: TaxPreparationData | null = null
  @observable accessor innerVm: TaxPrepOrganizerViewModel | null = null
  @observable
  accessor extractionStatus: TxfWorklayerJobExtractionStatus | null = null

  readonly switchYearDialogVm: SwitchYearDialogViewModel
  readonly connectWithProDialogVm: ConnectWithProDialogViewModel

  constructor(
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _txfWorklayerJobStore: TxfWorklayerJobStore,
    private readonly _infoGatheringStore: InfoGatheringStore,
    public readonly submittedJob: SubmittedJob,
    public readonly txfJobVm: TxfJobViewModel,
  ) {
    this.switchYearDialogVm = new SwitchYearDialogViewModel({
      onSave: (navigateTo) =>
        this.innerVm?.save(navigateTo) ?? Promise.resolve(),
      onNavigate: (year) => void this._proceedToYear(year),
    })
    this.connectWithProDialogVm = new ConnectWithProDialogViewModel({
      onConfirm: () => this._completeInfoGathering(),
    })
    this.lifecycle.manage(
      timer(0, 15000).pipe(
        takeWhile(
          () => !this.hasFilingYears && !this.isExtractionFinished,
          true,
        ),
        switchMap(() =>
          withAbortSignal((signal) =>
            from(this.checkExtractionStatus(signal)),
          ).pipe(
            tap(
              () =>
                this.extractionStatus ===
                  TxfWorklayerJobExtractionStatus.Failed &&
                this._navigateToConnectWithPro(),
            ),
          ),
        ),
      ),
    )
  }

  @computed
  get jobId() {
    return this.submittedJob.id
  }

  @computed
  get activeFilingYears(): FilingYear[] {
    return this.txfWorklayerJob?.activeFilingYears ?? []
  }

  @computed
  get canContinueToTaxPrepOrganizer(): boolean {
    const infoGatheringCompleted =
      this.checklist?.completion.type === 'Completed' ||
      (this.checklist?.completion.type === 'Incomplete' &&
        this.checklist.completion.allRequiredItemsCompleted)

    return this.txfJobVm.isEnabledAndTxfJob && infoGatheringCompleted
  }

  @computed
  get hasFilingYears(): boolean {
    return this.activeFilingYears.length > 0
  }

  @computed
  get txfJob() {
    return this.txfJobVm.txfJob
  }

  @computed
  get inactivityGracePeriodExpiresAt() {
    return this.txfJob?.onboardingState.inactivityGracePeriodExpiresAt
  }

  @computed
  get timer() {
    if (this.hasFilingYears) {
      return 0
    }
    return this._countDown()
  }

  @computed
  get showConnectWithAPro(): boolean {
    if (this.submittedJob.status !== 'InfoGathering') return false
    return (
      this.checklist?.completion.type === 'Completed' ||
      (this.checklist?.completion.type === 'Incomplete' &&
        this.checklist?.completion.allRequiredItemsCompleted)
    )
  }

  @computed
  get isExtractionFinished(): boolean {
    return (
      this.extractionStatus === TxfWorklayerJobExtractionStatus.Done ||
      this.extractionStatus === TxfWorklayerJobExtractionStatus.Failed
    )
  }

  fetch = task(async (signal?: AbortSignal) => {
    const [txfWorklayerJob, checklist] = await Promise.all([
      this._txfWorklayerJobStore.fetchTxfWorklayerJob(this.jobId, signal),
      this._infoGatheringStore.fetchChecklist(this.jobId, signal),
      this.txfJobVm.fetchTxfJob(signal),
    ])

    runInAction(() => {
      this.txfWorklayerJob = txfWorklayerJob
      this.checklist = checklist
    })

    if (
      this.txfWorklayerJob &&
      this.txfWorklayerJob.activeFilingYears.length > 0
    ) {
      await this._proceedToYear(this.txfWorklayerJob.activeFilingYears[0])
    }
  })

  fetchTaxPrepData = task(async (signal?: AbortSignal) => {
    if (this.selectedYear === null) return

    const data = await this._txfWorklayerJobStore.fetchTxfTaxPreparationData(
      this.jobId,
      this.selectedYear.year,
      signal,
    )
    if (!data) {
      throw new Error('Failed to fetch tax preparation data')
    }

    runInAction(() => {
      this.taxPrepData = data
      this._createInnerVm()
    })
  })

  checkExtractionStatus = task(async (signal?: AbortSignal) => {
    const extractionStatus =
      await this._txfWorklayerJobStore.getExtractionStatus(this.jobId, signal)

    runInAction(() => {
      this.extractionStatus = extractionStatus
    })

    if (this.extractionStatus === TxfWorklayerJobExtractionStatus.Done) {
      await this.fetch(signal)
    }
  })

  @action.bound
  private _createInnerVm() {
    if (
      this.innerVm ||
      !this.taxPrepData ||
      !this.txfWorklayerJob ||
      !this.selectedYear
    )
      return

    this.innerVm = new TaxPrepOrganizerViewModel(
      this._createSnackbarErrorHandler,
      this._newSnackbar,
      this._txfWorklayerJobStore,
      () => this.txfWorklayerJob!,
      () => this.selectedYear!,
      () => this.taxPrepData!,
      this.fetchTaxPrepData,
      this._proceedToYear,
    )
    this.lifecycle.manage(this.innerVm)
    this.innerVm.activate()
  }

  @action.bound
  selectYear(filingYear: FilingYear) {
    if (this.selectedYear === null || this.selectedYear.id === filingYear.id)
      return

    this.switchYearDialogVm.showDialog(filingYear)
  }

  private _proceedToYear = task(async (filingYear: FilingYear) => {
    runInAction(() => {
      this.selectedYear = filingYear
    })
    await this.fetchTaxPrepData()
  })

  @action.bound
  showConnectWithProDialog() {
    this.connectWithProDialogVm.showDialog()
  }

  private async _completeInfoGathering() {
    await this._infoGatheringStore
      .completeInfoGathering(this.jobId)
      .catch(
        this._createSnackbarErrorHandler('Failed to complete info gathering'),
      )

    const handle = this._getExternalUrlDelegate().createHandle({
      sameWindow: true,
    })
    handle.open(links.jobDetails(this.jobId))
  }

  private async _navigateToConnectWithPro() {
    await this._completeInfoGathering()
  }

  activate() {
    this.lifecycle.activate()
    this._disposeTimerReaction?.()
    this._disposeTimerReaction = reaction(
      () => this.timer,
      (timer) => {
        if (this.hasFilingYears) {
          return
        }

        switch (timer) {
          case 8:
            this._newSnackbar((s) =>
              s
                .type('warning')
                .autoDismiss()
                .saying(
                  `Please upload documents or contact Customer Support to continue, redirecting to info gathering...`,
                )
                .show(),
            )
            break
          case 2:
            void this._navigateToConnectWithPro()
            break
        }
      },
      { fireImmediately: true },
    )
  }

  deactivate() {
    this._disposeTimerReaction?.()
    this._disposeTimerReaction = null
    this.lifecycle.deactivate()
  }
}
